import { ViewToggle } from './view-toggle';
import { Dropdown, PRICE_SLIDER_MAX } from './dropdowns';
import { RanchTypeFilterHover } from './ranch-type-filter-hover';
import { PostMapLink } from './post-map-link';

const SELECTORS = {
    block: '.wp-block-ssm-interactive-map',
    rangeSlider: '.range-slider-wrap',
    toggleViewButton: '.wp-block-ssm-interactive-map__dropdown-toggle',
    content: '.wp-block-ssm-interactive-map__content',
    submitButton: '.wp-block-ssm-interactive-map__submit',
};

// Capped range sliders, keyed by FacetWP facet name -> capped max value.
// Kept in sync with the $caps map in ssm.php's facetwp_index_row filter.
const SLIDER_CAPS = {
    ranch_price: PRICE_SLIDER_MAX,
    ranch_acreage: 10000,
};

export const InteractiveMap = () => {
    document.querySelectorAll(SELECTORS.block).forEach((block) => {
        initInteractiveMap(block);
    });

    // Scroll to results if FacetWP params are in URL
    scrollToResultsOnFacetURL();

    // Keep pagination scroll anchored to the results, not the top of the page
    handlePaginationScroll();

    // Show "$20,000,000+" / "10k+" on the sliders once the top handle hits the cap
    initSliderLabels();
};

export const initInteractiveMap = (block) => {
    const viewToggle = new ViewToggle(block);

    const dropdownToggles = block.querySelectorAll(SELECTORS.toggleViewButton);
    dropdownToggles.forEach((toggle) => {
        new Dropdown(toggle);
    });

    initFacetSubmit(block);

    new RanchTypeFilterHover(block);

    new PostMapLink(block, viewToggle);
};

const initFacetSubmit = (block) => {
    const submitButton = block.querySelector(SELECTORS.submitButton);
    if (!submitButton) return;

    submitButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (typeof FWP === 'undefined') return;

        // Always trigger refresh - FWP will handle the filtering
        FWP.refresh();
    });
};

const scrollToResultsOnFacetURL = () => {
    const urlParams = new URLSearchParams(window.location.search);

    // Check if any parameter starts with 'fwp_'
    const hasFacetParams = Array.from(urlParams.keys()).some((key) => key.startsWith('fwp_'));

    if (!hasFacetParams) return;

    const template = document.querySelector(SELECTORS.block);
    if (!template) return;

    setTimeout(() => {
        template.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 1500);
};

// FacetWP redraws a slider's inline "$X — $Y" label on every drag via the
// `facetwp/set_label/slider` action, keeping the live upper-handle value in
// FWP.settings[facet].upper. For each capped slider (see SLIDER_CAPS) we append
// "+" once that value reaches the cap so the label reads "$20,000,000+" / "10k+"
// — signalling it includes every ranch at or above the cap (the cap itself is
// enforced in ssm.php's facetwp_index_row filter). Coverage comes from two paths:
//   1. A later-priority action on the same hook — fires on every drag update.
//   2. A direct pass on `facetwp-loaded` — covers the initial paint in case FWP
//      wasn't ready when we first registered the action.
const isUpperAtCap = (facetName) => {
    const upper = parseFloat(FWP?.settings?.[facetName]?.upper);

    return !isNaN(upper) && upper >= SLIDER_CAPS[facetName];
};

const appendPlus = (labelNode) => {
    if (!labelNode) return;

    const html = labelNode.innerHTML;
    if (!html.trimEnd().endsWith('+')) {
        labelNode.innerHTML = `${html}+`;
    }
};

const registerSliderLabelHook = () => {
    if (typeof FWP === 'undefined' || !FWP.hooks || FWP.__sliderLabelCapHooked) return;

    FWP.__sliderLabelCapHooked = true;

    // Priority 20 runs after FacetWP's default label builder (priority 10)
    FWP.hooks.addAction(
        'facetwp/set_label/slider',
        ($facet) => {
            const facetName = $facet.attr('data-name');

            if (!(facetName in SLIDER_CAPS) || !isUpperAtCap(facetName)) return;

            appendPlus($facet.find('.facetwp-slider-label').nodes?.[0]);
        },
        20,
    );
};

const initSliderLabels = () => {
    registerSliderLabelHook();

    document.addEventListener('facetwp-loaded', () => {
        registerSliderLabelHook();

        if (typeof FWP === 'undefined') return;

        Object.keys(SLIDER_CAPS).forEach((facetName) => {
            if (!isUpperAtCap(facetName)) return;

            appendPlus(
                document.querySelector(`.facetwp-facet[data-name="${facetName}"] .facetwp-slider-label`),
            );
        });
    });
};

const handlePaginationScroll = () => {
    // `facetwp-loaded` fires (front.js:498) before FacetWP's own scroll hook
    // (front.js:501, priority 100). Clearing FWP.scroll_target here makes that
    // hook's `document.querySelector('')` throw and get swallowed, so FacetWP's
    // window scroll to the top of the page never happens and we scroll ourselves.
    //
    // Load More appends results below the current ones, so it must not scroll.
    // FacetWP clears FWP.is_load_more in its template_html filter, before
    // `facetwp-loaded` fires (front-facets.js:1125), so capture it on refresh.
    let isLoadMore = false;

    document.addEventListener('facetwp-refresh', () => {
        isLoadMore = typeof FWP !== 'undefined' && FWP.is_load_more;
    });

    document.addEventListener('facetwp-loaded', () => {
        if (typeof FWP === 'undefined') return;

        // FWP.loaded is still false during the very first load's event and only
        // flips to true afterwards (front.js:513), so this skips the initial render.
        if (!FWP.loaded) return;

        if (isLoadMore) return;

        const block = document.querySelector(SELECTORS.block);
        if (!block) return;

        // Cancel FacetWP's default window scroll (jumps to the top of the page).
        FWP.scroll_target = '';
        FWP.scroll_offset = '';

        // Map view: the content is a fixed-height internal scroll container that
        // Lenis leaves alone (whitelisted in the Lenis `prevent` list), so reset
        // it natively to bring the first posts back into view.
        if (block.classList.contains('is-view-map')) {
            const content = block.querySelector(SELECTORS.content);
            if (content) content.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Scroll the page up to the top of the block in both views. Lenis owns the
        // window scroll, so go through it — native scrollIntoView/window.scrollTo
        // would fight Lenis's RAF loop and skip ScrollTrigger.update.
        if (window.lenisInstance) {
            window.lenisInstance.scrollTo(block, { offset: 0 });
        } else {
            block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });
};
